import { Injectable } from '@angular/core';
import {
  ConsultationFormAssignmentResponse,
  ConsultationFormResponse,
  FormStatus,
  IntakeFormApi,
  IntakeFormRequest,
  Page,
  SaveIntakeFormProgressRequest,
  SignIntakeFormRequest,
} from '@models';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';
import { IntakeFormsService } from './intake-forms.interface';

@Injectable()
export class IntakeFormsMockService extends IntakeFormsService {
  private mockData: IntakeFormApi[] = [
    {
      id: '1',
      patientName: 'John Doe',
      providerName: 'Dr. Brown',
      status: FormStatus.Sent,
      requestedAtUtc: '2026-05-10T10:00:00Z',
      completedAtUtc: null,
      createdAtUtc: '2026-05-10T10:00:00Z',
    },
    {
      id: '2',
      patientName: 'Jane Smith',
      providerName: 'Dr. Wilson',
      status: FormStatus.Completed,
      requestedAtUtc: '2026-05-08T10:00:00Z',
      completedAtUtc: '2026-05-09T11:00:00Z',
      createdAtUtc: '2026-05-08T10:00:00Z',
    },
    {
      id: '3',
      patientName: 'Alice Johnson',
      providerName: 'Dr. Taylor',
      status: FormStatus.Opened,
      requestedAtUtc: '2026-05-11T09:00:00Z',
      completedAtUtc: null,
      createdAtUtc: '2026-05-11T09:00:00Z',
    },
    {
      id: '4',
      patientName: 'Bob Lee',
      providerName: 'Dr. Anderson',
      status: FormStatus.Sent,
      requestedAtUtc: '2026-05-12T11:00:00Z',
      completedAtUtc: null,
      createdAtUtc: '2026-05-12T11:00:00Z',
    },
  ];

  override getIntakeForms(
    params: IntakeFormRequest
  ): Observable<Page<IntakeFormApi>> {
    let data = [...this.mockData];

    if (params.status) {
      data = data.filter(f => f.status === params.status);
    }

    if (params.patientName) {
      const search = params.patientName.toLowerCase();
      data = data.filter(f => f.patientName.toLowerCase().includes(search));
    }

    if (params.orderBy === 'requestedAtUtc') {
      data.sort((a, b) => {
        const aTime = new Date(a.requestedAtUtc ?? '').getTime();
        const bTime = new Date(b.requestedAtUtc ?? '').getTime();
        return params.desc ? bTime - aTime : aTime - bTime;
      });
    }

    const start = (params.page - 1) * params.pageSize;
    const items = data.slice(start, start + params.pageSize);

    return of({
      items,
      totalItems: data.length,
      pageNumber: params.page,
      pageSize: params.pageSize,
      totalPages: Math.ceil(data.length / params.pageSize),
    }).pipe(delay(500));
  }

  override getIntakeForm(id: string): Observable<ConsultationFormResponse> {
    const item = this.mockData.find(f => f.id === id);
    if (!item) {
      return throwError(() => new Error('Intake form not found')).pipe(
        delay(300)
      );
    }
    return of({
      definition: {
        templateTranslationId: 'mock-template-translation-id',
        language: 'en',
        name: 'Consultation Form',
        template: {
          templateId: 'intake-form-template-v1',
          version: 1,
          categories: [],
          dynamicOptions: [],
          sections: [],
        },
      },
      submission: item,
    }).pipe(delay(400));
  }

  override assignToPatient(
    patientId: string
  ): Observable<ConsultationFormAssignmentResponse> {
    const newId = (this.mockData.length + 1).toString();
    const newForm: IntakeFormApi = {
      id: newId,
      patientName: `Patient ${patientId}`,
      providerName: 'Dr. Mock',
      status: FormStatus.Sent,
      requestedAtUtc: new Date().toISOString(),
      completedAtUtc: null,
      createdAtUtc: new Date().toISOString(),
    };

    this.mockData.push(newForm);
    return of({ submissionId: newId, created: true }).pipe(delay(400));
  }

  override sign(
    submissionId: string,
    body: SignIntakeFormRequest
  ): Observable<string> {
    const item = this.mockData.find(f => f.id === submissionId);

    if (!item) {
      return throwError(() => new Error('Submission not found')).pipe(
        delay(300)
      );
    }

    if (item.status === FormStatus.Completed) {
      return throwError(() => new Error('Already signed')).pipe(delay(300));
    }

    const now = new Date().toISOString();
    item.status = FormStatus.Completed;
    item.completedAtUtc = now;
    item.signature = body.signature ?? undefined;
    item.answers = body.answers ?? undefined;

    return of('Signed').pipe(delay(500));
  }

  override deleteSubmission(submissionId: string): Observable<unknown> {
    this.mockData = this.mockData.filter(f => f.id !== submissionId);
    return of(undefined).pipe(delay(300));
  }

  override getPdf(
    _submissionId: string,
    _action: 'Download' | 'View' = 'Download'
  ): Observable<Blob> {
    return of(new Blob([], { type: 'application/pdf' }));
  }

  override markAsOpened(submissionId: string): Observable<void> {
    this.mockData = this.mockData.map(form => {
      if (form.id !== submissionId) return form;
      if (form.status !== FormStatus.Sent) return form;
      return { ...form, status: FormStatus.Opened };
    });
    return of(undefined as void);
  }

  override saveProgress(
    _submissionId: string,
    _body: SaveIntakeFormProgressRequest
  ): Observable<unknown> {
    return of(undefined).pipe(delay(200));
  }

  override complete(
    submissionId: string,
    body: SignIntakeFormRequest
  ): Observable<unknown> {
    const item = this.mockData.find(f => f.id === submissionId);
    if (item) {
      item.status = FormStatus.Completed;
      item.completedAtUtc = new Date().toISOString();
      item.signature = body.signature ?? undefined;
      item.answers = body.answers ?? undefined;
    }
    return of(undefined).pipe(delay(300));
  }
}
